import { useState, useEffect, useMemo, useRef } from 'react'; // useEffect: page data only
import { isIndian, invoiceGst, gstConflict, paidGstPosting, gstInputFrom, gstPercentsOf, type GstInput } from '../utils/tradingSystem';
import { PaidGstFields } from '../components/GstBoxes';
import {
  collection, onSnapshot, doc, runTransaction, serverTimestamp,
  deleteDoc, updateDoc, setDoc,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { usePaging } from '../hooks/usePaging';
import { useFitHeight } from '../hooks/useFitHeight';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { MoneyCalendar, ViewToggle } from '../components/MoneyCalendar';
import { localISO } from '../utils/localDate';
import { Paging, DeleteIconButton } from '../components/ui';
import { useNewParam } from '../hooks/useNewParam';
import { Button, Modal, ConfirmModal, FormField, EntityCombobox } from '../components/ui';
import { HeaderSearch, DateRangeButton, StatChips, IconPlusSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney, CURRENCY_LIST, findExchangeRate } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import {
  EXPENSE_CATEGORIES, buildEntry, postEntry, entryId, linesExpense,
} from '../utils/ledger';
import { generateDueExpenses } from '../utils/recurringExpenses';
import type { Expense, Entity, BankAccount, RecurringExpense } from '../types';

// ── Helpers (per-page, matching convention) ───────────────────────────────
function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ── Add / Edit modal ─────────────────────────────────────────────────────

interface FormState {
  category: string; vendorEntityId: string; description: string;
  amount: string; currency: string; date: string; dueDate: string;
  payNow: boolean; bankAccountId: string;
}
const EMPTY: FormState = {
  category: '', vendorEntityId: '', description: '', amount: '', currency: '',
  date: todayISO(), dueDate: '', payNow: false, bankAccountId: '',
};

function ExpenseModal({ editing, entities, accounts, onClose }: {
  editing: Expense | null;
  entities: Entity[];
  accounts: BankAccount[];
  onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const isEdit = !!editing;
  // Modal mounts fresh each open (conditional render), so init straight from
  // props — no effect needed.
  const [form, setForm] = useState<FormState>(() =>
    editing
      ? {
          category: editing.category, vendorEntityId: editing.vendorEntityId ?? '',
          // Indian: the box holds the amount before GST.
          description: editing.description, amount: String(editing.gstReverseCharge ? editing.amountUSD : Math.round((editing.amountUSD - (editing.gstTotalUSD ?? 0)) * 100) / 100),
          currency: editing.currency,
          date: editing.date, dueDate: editing.dueDate ?? '',
          payNow: false, bankAccountId: '',
        }
      : { ...EMPTY, currency: settings.currency, bankAccountId: (accounts.find(a => a.isDefault) ?? accounts[0])?.id ?? '' },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Indian trading system: GST on this expense (claimable unless ticked).
  const indian = isIndian(settings);
  const [gstIn, setGstIn] = useState<GstInput>(() => gstInputFrom(editing?.gst));
  const [gstNotClaimable, setGstNotClaimable] = useState(!!editing?.gstNotClaimable);
  const [gstReverse, setGstReverse] = useState(!!editing?.gstReverseCharge);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(f => ({ ...f, [k]: v }));
  const acct = accounts.find(a => a.id === form.bankAccountId) ?? null;
  // Paying immediately follows the chosen account's own currency (unchanged
  // behavior); otherwise the user picks the currency directly — an unpaid
  // bill doesn't have to be entered in the base currency just because
  // there's no account attached yet.
  const currency = form.payNow && acct ? acct.currency : form.currency;

  async function handleSave() {
    const baseAmt = parseFloat(form.amount);
    // Indian: what we pay = amount + GST; the claimable GST goes to Input GST.
    const gstPcts = indian ? gstPercentsOf(gstIn) : {};
    if (indian && gstConflict(gstPcts)) return setError('Use CGST + SGST, or IGST, not both.');
    const gstTotal = indian && baseAmt > 0 ? invoiceGst(baseAmt, gstPcts).total : 0;
    // Reverse charge: the seller charged no GST, so we pay them the amount only.
    const amt = gstTotal > 0 && !gstReverse ? Math.round((baseAmt + gstTotal) * 100) / 100 : baseAmt;
    const gstFields = gstTotal > 0
      ? { gst: { cgstPct: gstPcts.cgstPct ?? null, sgstPct: gstPcts.sgstPct ?? null, igstPct: gstPcts.igstPct ?? null },
          gstTotalUSD: gstTotal, gstNotClaimable: gstNotClaimable || null, gstReverseCharge: gstReverse || null }
      : { gst: null, gstTotalUSD: null, gstNotClaimable: null, gstReverseCharge: null };
    const gstPosting = paidGstPosting(gstFields);
    if (!form.category) return setError('Choose a category.');
    if (!form.description.trim()) return setError('Enter a short description.');
    if (isNaN(amt) || amt <= 0) return setError('Enter a valid amount.');
    if (!form.date) return setError('Pick the date.');
    if (!isEdit && form.payNow && !form.bankAccountId) return setError('Choose the account you paid from.');

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    try {
      if (isEdit) {
        // Edit fields only. If the expense is already paid, keep the bank
        // cascade in sync (delta on balance + the linked cash-book row).
        const expRef = doc(db, 'expenses', editing.id);
        const paid = editing.paymentStatus === 'paid';
        const accRef = paid && editing.bankAccountId ? doc(db, 'bankAccounts', editing.bankAccountId) : null;
        // If this expense was paid with a currency conversion
        // (editing.bankCurrency set), re-derive the new bank-side amount
        // proportionally via the stored exchangeRate instead of applying the
        // raw base-currency delta — mirrors AP.tsx/AR.tsx's own edit-payment
        // fix. Unconverted (same-currency) payments keep today's exact
        // behavior: bankDiff === the raw delta.
        const newBankAmt = editing.exchangeRate ? amt * editing.exchangeRate : amt;
        const oldBankAmt = editing.bankAmount ?? editing.amountUSD;
        const bankDiff = newBankAmt - oldBankAmt;
        await runTransaction(db, async tx => {
          const accSnap = accRef ? await tx.get(accRef) : null;
          tx.update(expRef, stripUndef({
            category: form.category,
            vendorEntityId: form.vendorEntityId || null,
            description: form.description.trim(),
            amountUSD: amt,
            ...gstFields,
            paidUSD: paid ? amt : 0,
            bankAmount: paid && editing.bankCurrency ? newBankAmt : undefined,
            date: form.date,
            dueDate: form.dueDate || null,
            updatedAt: serverTimestamp(),
          }));
          if (paid && accRef && accSnap?.exists()) {
            tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance - bankDiff });
            if (editing.bankTransactionId) {
              tx.update(doc(db, 'bankTransactions', editing.bankTransactionId), {
                amount: newBankAmt, category: form.category, description: form.description.trim(),
              });
            }
          }
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'expense', sourceId: editing.id,
            sourceCollection: 'expenses', date: form.date, currency,
            createdBy: user?.id ?? editing.createdBy,
            note: form.description.trim(),
            lines: linesExpense(amt, form.category,
              paid && editing.bankAccountId ? { bankAccountId: editing.bankAccountId } : 'accrued',
              form.vendorEntityId || undefined, gstPosting),
          }), entryId('expense', editing.id));
        });
      } else {
        const expRef = doc(collection(db, 'expenses'));
        const payNow = form.payNow && !!form.bankAccountId;
        const accRef = payNow ? doc(db, 'bankAccounts', form.bankAccountId) : null;
        const txRef  = payNow ? doc(collection(db, 'bankTransactions')) : null;
        await runTransaction(db, async tx => {
          const accSnap = accRef ? await tx.get(accRef) : null;
          if (accRef && !accSnap?.exists()) throw new Error('That bank account no longer exists.');

          tx.set(expRef, stripUndef({
            businessId: business.id,
            category: form.category,
            vendorEntityId: form.vendorEntityId || undefined,
            description: form.description.trim(),
            amountUSD: amt,
            ...(gstTotal > 0 ? gstFields : {}),
            currency,
            date: form.date,
            dueDate: payNow ? undefined : (form.dueDate || undefined),
            paymentStatus: payNow ? 'paid' : 'unpaid',
            paidUSD: payNow ? amt : 0,
            bankAccountId: payNow ? acct!.id : undefined,
            bankAccountName: payNow ? acct!.name : undefined,
            paidDate: payNow ? form.date : undefined,
            bankTransactionId: payNow ? txRef!.id : undefined,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: user!.id,
          }));

          if (payNow && accRef && accSnap && txRef) {
            tx.set(txRef, stripUndef({
              businessId: business.id, accountId: acct!.id, accountName: acct!.name,
              currency: acct!.currency, direction: 'out', amount: amt, date: form.date,
              description: form.description.trim(), referenceType: 'expense',
              referenceId: expRef.id, category: form.category,
              createdAt: new Date().toISOString(), createdBy: user!.id,
            }));
            tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance - amt });
          }

          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'expense', sourceId: expRef.id,
            sourceCollection: 'expenses', date: form.date, currency,
            createdBy: user!.id, note: form.description.trim(),
            lines: linesExpense(amt, form.category,
              payNow ? { bankAccountId: acct!.id } : 'accrued',
              form.vendorEntityId || undefined, gstPosting),
          }), entryId('expense', expRef.id));
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the expense.');
    }
    setSaving(false);
  }

  return (
    <Modal
      open onClose={onClose}
      title={isEdit ? 'Edit Expense' : 'Add Expense'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>{isEdit ? 'Save' : 'Add Expense'}</Button>
        </>
      }
    >
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="form-row-2">
        <FormField label="Category" required>
          <select value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="">Select…</option>
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={form.date} max={todayISO()} onChange={e => set('date', e.target.value)} />
        </FormField>
      </div>
      <FormField label="Description" required>
        <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="What was this for?" />
      </FormField>
      <div className="form-row-2">
        <FormField label={indian ? 'Amount (before GST)' : 'Amount'} required>
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="number" step="0.01" min="0.01" value={form.amount}
              onChange={e => set('amount', e.target.value)} className="no-spinner" style={{ flex: 1, minWidth: 0 }} />
            <select
              value={currency}
              onChange={e => set('currency', e.target.value)}
              disabled={form.payNow && !!acct}
              title={form.payNow && acct ? 'Follows the paid-from account\'s currency' : 'Currency'}
              style={{ width: 88, flexShrink: 0 }}
            >
              {CURRENCY_LIST.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
        </FormField>
        <FormField label="Vendor (optional)">
          <EntityCombobox
            entities={entities.map(e => ({ id: e.id, companyName: e.companyName }))}
            value={form.vendorEntityId}
            onChange={v => set('vendorEntityId', v)}
            clearLabel="No vendor"
          />
        </FormField>
      </div>

      {indian && (
        <PaidGstFields base={parseFloat(form.amount) || 0} value={gstIn} onChange={setGstIn}
          notClaimable={gstNotClaimable} onNotClaimable={setGstNotClaimable}
          reverseCharge={gstReverse} onReverseCharge={setGstReverse}
          money={v => fmtMoney(v, currency)} />
      )}

      {!isEdit && (
        <div style={{ marginTop: 6, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.payNow} onChange={e => set('payNow', e.target.checked)} />
            I paid this now
          </label>
          {form.payNow ? (
            <div style={{ marginTop: 10 }}>
              <FormField label="Paid from" required>
                <select value={form.bankAccountId} onChange={e => set('bankAccountId', e.target.value)}>
                  <option value="">Select account…</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
                </select>
              </FormField>
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <FormField label="Due date (optional)" hint="When this bill needs paying">
                <input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
              </FormField>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Pay modal (unpaid → paid) ────────────────────────────────────────────

function PayModal({ expense, accounts, onClose }: {
  expense: Expense; accounts: BankAccount[]; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const [bankAccountId, setBankAccountId] = useState(
    accounts.find(a => a.id === expense.preferredBankAccountId)?.id
    ?? accounts.find(a => a.isDefault)?.id
    ?? accounts[0]?.id
    ?? '',
  );
  const [paidDate, setPaidDate] = useState(todayISO());
  const [rate, setRate] = useState('');
  const [rateTouched, setRateTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const acct = accounts.find(a => a.id === bankAccountId) ?? null;

  // The bill is fixed in expense.currency — conversion only kicks in when
  // the account being paid from uses a different one. Mirrors
  // RecordPaymentModal.tsx's own needsConversion/rate/bankAmt exactly.
  const needsConversion = !!acct && acct.currency !== expense.currency;
  const configuredRate = needsConversion ? findExchangeRate(settings.exchangeRates, expense.currency, acct!.currency) : null;

  useEffect(() => { setRateTouched(false); }, [bankAccountId]);
  useEffect(() => {
    if (!needsConversion || rateTouched || !configuredRate) return;
    setRate(String(configuredRate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsConversion, rateTouched, configuredRate]);

  const rateNum  = needsConversion ? (parseFloat(rate) || 0) : 1;
  const bankAmt  = needsConversion ? expense.amountUSD * rateNum : expense.amountUSD;
  const afterBalance = acct ? acct.currentBalance - bankAmt : null;

  async function pay() {
    if (!bankAccountId) return setError('Choose an account.');
    if (needsConversion) {
      const r = parseFloat(rate);
      if (!rate || isNaN(r) || r <= 0) return setError(`Enter the conversion rate to ${acct!.currency} — 1 ${expense.currency} equals how many ${acct!.currency}.`);
    }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business || !acct) { setError('Not connected.'); setSaving(false); return; }
    try {
      const expRef = doc(db, 'expenses', expense.id);
      const accRef = doc(db, 'bankAccounts', acct.id);
      const txRef  = doc(collection(db, 'bankTransactions'));
      await runTransaction(db, async tx => {
        const accSnap = await tx.get(accRef);
        if (!accSnap.exists()) throw new Error('That bank account no longer exists.');
        tx.update(expRef, stripUndef({
          paymentStatus: 'paid', paidUSD: expense.amountUSD,
          bankAccountId: acct.id, bankAccountName: acct.name,
          paidDate, bankTransactionId: txRef.id, updatedAt: serverTimestamp(),
          bankAmount:   needsConversion ? bankAmt : undefined,
          bankCurrency: needsConversion ? acct.currency : undefined,
          exchangeRate: needsConversion ? rateNum : undefined,
        }));
        tx.set(txRef, stripUndef({
          businessId: business.id, accountId: acct.id, accountName: acct.name,
          currency: acct.currency, direction: 'out', amount: bankAmt, date: paidDate,
          description: expense.description, referenceType: 'expense', referenceId: expense.id,
          category: expense.category, createdAt: new Date().toISOString(), createdBy: user!.id,
        }));
        tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance - bankAmt });
        // Overwrite the expense's entry: credit moves from Accrued → the
        // bank. Stays in the expense's own currency — GL never converts.
        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'expense', sourceId: expense.id,
          sourceCollection: 'expenses', date: expense.date, currency: expense.currency,
          createdBy: user?.id ?? expense.createdBy, note: expense.description,
          lines: linesExpense(expense.amountUSD, expense.category, { bankAccountId: acct.id }, expense.vendorEntityId, paidGstPosting(expense)),
        }), entryId('expense', expense.id));
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the payment.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} title="Pay Expense"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={pay} loading={saving}>Mark Paid</Button>
      </>}>
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
      <div style={{ fontSize: 13, marginBottom: 14, color: 'var(--text-2)' }}>
        {expense.description} — <strong>{fmtMoney(expense.amountUSD, expense.currency)}</strong>
      </div>
      <div className="form-row-2">
        <FormField label="Paid from" required>
          <select value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}>
            <option value="">Select…</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
          </select>
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={paidDate} max={todayISO()} onChange={e => setPaidDate(e.target.value)} />
        </FormField>
      </div>

      {needsConversion && (
        <div className="form-row-2">
          <FormField
            label={`Conversion Rate (1 ${expense.currency} = ? ${acct!.currency})`}
            required
            hint={configuredRate
              ? `Pre-filled from Settings › Dashboard's Exchange Rates card — adjust if the bank's actual rate today differs.`
              : `No exchange rate configured for ${expense.currency} → ${acct!.currency} — enter today's rate (or set one on the Dashboard's Exchange Rates card to auto-fill this next time).`}
          >
            <input
              className="no-spinner"
              type="number" step="0.0001" min="0.0001"
              value={rate}
              onChange={e => { setRate(e.target.value); setRateTouched(true); }}
              placeholder="0.0000"
            />
          </FormField>
          <FormField label={`Converted Amount (${acct!.currency})`}>
            <input value={bankAmt > 0 ? bankAmt.toFixed(2) : ''} placeholder="0.00" disabled />
          </FormField>
        </div>
      )}
      {needsConversion && afterBalance != null && (
        <p className="form-hint">After this payment: {fmtMoney(afterBalance, acct!.currency)}</p>
      )}
    </Modal>
  );
}

// ── Recurring Expenses (templates) ──────────────────────────────────────
// Manages the small set of planned/repeating bills — this modal never
// touches the `expenses` collection itself; generateDueExpenses (called
// from the page on load) is what turns a due template-period into a normal
// unpaid Expense doc.

interface TplFormState {
  category: string; vendorEntityId: string; description: string;
  amount: string; currency: string; dayOfMonth: string; notes: string;
  bankAccountId: string;
}
const TPL_EMPTY: TplFormState = {
  category: '', vendorEntityId: '', description: '', amount: '', currency: '', dayOfMonth: '1', notes: '',
  bankAccountId: '',
};

function RecurringExpensesModal({ templates, entities, accounts, onClose }: {
  templates: RecurringExpense[];
  entities: Entity[];
  accounts: BankAccount[];
  onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const [form, setForm] = useState<TplFormState | null>(null); // null = list view
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<RecurringExpense | null>(null);

  const entityName = (id?: string) => (id ? entities.find(e => e.id === id)?.companyName ?? '—' : '—');

  function openAdd() {
    setEditingId(null);
    setForm({ ...TPL_EMPTY, currency: settings.currency });
    setError('');
  }
  function openEdit(t: RecurringExpense) {
    setEditingId(t.id);
    setForm({
      category: t.category, vendorEntityId: t.vendorEntityId ?? '', description: t.description,
      amount: String(t.amount), currency: t.currency, dayOfMonth: String(t.dayOfMonth), notes: t.notes ?? '',
      bankAccountId: t.defaultBankAccountId ?? '',
    });
    setError('');
  }

  async function save() {
    if (!form) return;
    const amt = parseFloat(form.amount);
    const day = parseInt(form.dayOfMonth, 10);
    if (!form.category) return setError('Choose a category.');
    if (!form.description.trim()) return setError('Enter a short description.');
    if (isNaN(amt) || amt <= 0) return setError('Enter a valid amount.');
    if (isNaN(day) || day < 1 || day > 31) return setError('Day of month must be between 1 and 31.');

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business || !user) { setError('Not connected.'); setSaving(false); return; }
    try {
      if (editingId) {
        await updateDoc(doc(db, 'recurringExpenses', editingId), stripUndef({
          category: form.category,
          vendorEntityId: form.vendorEntityId || null,
          description: form.description.trim(),
          amount: amt,
          currency: form.currency,
          dayOfMonth: day,
          notes: form.notes.trim() || null,
          defaultBankAccountId: form.bankAccountId || null,
          updatedAt: serverTimestamp(),
        }));
      } else {
        await setDoc(doc(collection(db, 'recurringExpenses')), stripUndef({
          businessId: business.id,
          category: form.category,
          vendorEntityId: form.vendorEntityId || undefined,
          description: form.description.trim(),
          amount: amt,
          currency: form.currency,
          dayOfMonth: day,
          notes: form.notes.trim() || undefined,
          defaultBankAccountId: form.bankAccountId || undefined,
          isActive: true,
          startPeriod: new Date().toISOString().slice(0, 7),
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: user.id,
        }));
      }
      setForm(null); setEditingId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the recurring expense.');
    }
    setSaving(false);
  }

  async function togglePause(t: RecurringExpense) {
    const db = getClientDb();
    if (!db) return;
    await updateDoc(doc(db, 'recurringExpenses', t.id), { isActive: !t.isActive, updatedAt: serverTimestamp() });
  }

  async function doDeleteTpl(t: RecurringExpense) {
    const db = getClientDb();
    if (!db) return;
    await deleteDoc(doc(db, 'recurringExpenses', t.id));
    setConfirmDelete(null);
  }

  return (
    <Modal
      open onClose={onClose} size="lg"
      title="Recurring Expenses"
      subtitle="Planned bills — rent, electricity, salaries — that auto-generate as an unpaid expense each period."
      footer={form ? (
        <>
          <Button variant="secondary" onClick={() => setForm(null)} disabled={saving}>Back</Button>
          <Button variant="primary" onClick={save} loading={saving}>{editingId ? 'Save' : 'Add Template'}</Button>
        </>
      ) : (
        <Button variant="secondary" onClick={onClose}>Close</Button>
      )}
    >
      {form ? (
        <>
          {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="form-row-2">
            <FormField label="Category" required>
              <select value={form.category} onChange={e => setForm(f => f && { ...f, category: e.target.value })}>
                <option value="">Select…</option>
                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
            <FormField label="Day of Month" required hint="Clamped to the last real day of short months">
              <input type="number" min="1" max="31" value={form.dayOfMonth} className="no-spinner"
                onChange={e => setForm(f => f && { ...f, dayOfMonth: e.target.value })} />
            </FormField>
          </div>
          <FormField label="Description" required>
            <input value={form.description} onChange={e => setForm(f => f && { ...f, description: e.target.value })} placeholder="e.g. Office Rent" />
          </FormField>
          <div className="form-row-2">
            <FormField label="Amount" required>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="number" step="0.01" min="0.01" value={form.amount} className="no-spinner"
                  onChange={e => setForm(f => f && { ...f, amount: e.target.value })} style={{ flex: 1, minWidth: 0 }} />
                <select value={form.currency} onChange={e => setForm(f => f && { ...f, currency: e.target.value })} style={{ width: 88, flexShrink: 0 }}>
                  {CURRENCY_LIST.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </div>
            </FormField>
            <FormField label="Vendor (optional)">
              <EntityCombobox
                entities={entities.map(e => ({ id: e.id, companyName: e.companyName }))}
                value={form.vendorEntityId}
                onChange={v => setForm(f => f && { ...f, vendorEntityId: v })}
                clearLabel="No vendor"
              />
            </FormField>
          </div>
          <FormField label="Default Bank Account (optional)" hint="Pre-fills the account when you mark a generated bill paid — still changeable each time">
            <select value={form.bankAccountId} onChange={e => setForm(f => f && { ...f, bankAccountId: e.target.value })}>
              <option value="">No default — choose each time</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
            </select>
          </FormField>
          <FormField label="Notes (optional)">
            <input value={form.notes} onChange={e => setForm(f => f && { ...f, notes: e.target.value })} />
          </FormField>
        </>
      ) : (
        <>
          <div style={{ marginBottom: 14 }}>
            <Button variant="primary" size="sm" onClick={openAdd}>+ Add Recurring Expense</Button>
          </div>
          {templates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 12px', color: 'var(--text-3)', fontSize: 13 }}>
              No recurring expenses yet — rent, electricity, salaries…
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {templates.map(t => (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
                  opacity: t.isActive ? 1 : 0.55,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>{t.description}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                      {t.category} · Day {t.dayOfMonth}
                      {t.vendorEntityId ? ` · ${entityName(t.vendorEntityId)}` : ''}
                      {t.defaultBankAccountId ? ` · ${accounts.find(a => a.id === t.defaultBankAccountId)?.name ?? 'Unknown account'}` : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontFamily: 'var(--font-num)', fontWeight: 600, color: 'var(--text-1)' }}>
                    {fmtMoney(t.amount, t.currency)}
                  </div>
                  <span className="badge" style={{
                    fontSize: 10, fontWeight: 700,
                    background: t.isActive ? 'var(--success-bg)' : 'var(--surface-2)',
                    color: t.isActive ? 'var(--success)' : 'var(--text-3)',
                  }}>{t.isActive ? 'Active' : 'Paused'}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(t)}>Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => togglePause(t)}>{t.isActive ? 'Pause' : 'Resume'}</Button>
                    <DeleteIconButton onClick={() => setConfirmDelete(t)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {confirmDelete && (
        <ConfirmModal
          open
          title="Delete this recurring expense?"
          message={`"${confirmDelete.description}" will no longer generate new bills. Expenses it already created are not affected.`}
          confirmLabel="Delete"
          onConfirm={() => doDeleteTpl(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </Modal>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

/** Calendar day: a paid expense on its date, an unpaid bill on its due date (else its date). */
function calDayOf(e: Expense): string {
  return e.paymentStatus === 'paid' ? e.date : (e.dueDate || e.date);
}

export default function ExpensesPage() {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [recurringTemplates, setRecurringTemplates] = useState<RecurringExpense[]>([]);
  const [loading, setLoading] = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-month');
  // List or Calendar (like Receivable / Payable); the calendar has its own month arrows.
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [calYM, setCalYM] = useState(() => ({ y: new Date().getFullYear(), m: new Date().getMonth() }));
  const stepMonth = (d: number) => setCalYM(({ y, m }) => { const t = new Date(y, m + d, 1); return { y: t.getFullYear(), m: t.getMonth() }; });
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [searchQ, setSearchQ] = useState('');

  const [modal, setModal] = useState<'add' | { edit: Expense } | { pay: Expense } | null>(null);
  // Dashboard "+ New" opens this form via ?new=1.
  useNewParam(() => setModal('add'));
  const [confirm, setConfirm] = useState<{ kind: 'delete' | 'unpay'; exp: Expense } | null>(null);
  const [busy, setBusy] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [genMsg, setGenMsg] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'expenses'),
      snap => { setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() } as Expense))); setLoading(false); },
      () => setLoading(false));
    const u2 = onSnapshot(collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))), () => {});
    const u3 = onSnapshot(collection(db, 'bankAccounts'),
      snap => setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount)).filter(a => a.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))), () => {});
    const u4 = onSnapshot(collection(db, 'recurringExpenses'),
      snap => setRecurringTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() } as RecurringExpense))), () => {});
    return () => { u1(); u2(); u3(); u4(); };
  }, [business]);

  // Auto-generate any due recurring bills once per page load (no backend
  // cron exists in this app) — catch-up safe, see generateDueExpenses.
  const generatedRef = useRef(false);
  useEffect(() => {
    if (generatedRef.current) return;
    if (!business || !user || recurringTemplates.length === 0) return;
    generatedRef.current = true;
    const db = getClientDb();
    if (!db) return;
    generateDueExpenses(db, business, user, recurringTemplates)
      .then(n => { if (n > 0) setGenMsg(`Generated ${n} recurring bill${n === 1 ? '' : 's'} for this period.`); })
      .catch(() => {});
  }, [business, user, recurringTemplates]);

  useEffect(() => {
    if (!genMsg) return;
    const t = setTimeout(() => setGenMsg(''), 6000);
    return () => clearTimeout(t);
  }, [genMsg]);

  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const entityName = (id?: string) => (id ? entities.find(e => e.id === id)?.companyName ?? '—' : '—');

  const baseRows = useMemo(() => {
    if (!window) return [];
    return expenses
      .filter(e => viewMode === 'calendar' || inWindow(e.date, window))
      .filter(e => {
        const q = searchQ.trim().toLowerCase();
        return !q || [e.category, e.description, e.vendorEntityId ? entityName(e.vendorEntityId) : ''].some(v => (v ?? '').toLowerCase().includes(q));
      })
      .sort((a, b) => b.date.localeCompare(a.date) || b.createdAt?.localeCompare?.(a.createdAt) || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, window, searchQ, entities, viewMode]);
  // Excel-style filters on Category, Vendor and Status.
  const filterColumns = useMemo<FilterColumn<Expense>[]>(() => [
    { key: 'category', label: 'Category', get: e => e.category },
    { key: 'vendor',   label: 'Vendor',   get: e => (e.vendorEntityId ? entityName(e.vendorEntityId) : '') },
    { key: 'status',   label: 'Status',   get: e => (e.paymentStatus === 'paid' ? 'Paid' : 'Unpaid') },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entities]);
  const { rows, filters: colFilters } = useColumnFilters(baseRows, filterColumns);
  const rowsTotal = useMemo(() => rows.reduce((s, e) => s + (e.amountUSD ?? 0), 0), [rows]);
  // 50 rows at a time; a new filter goes back to page 1.
  const pg = usePaging(rows, [JSON.stringify(window), searchQ, colFilters.stamp].join('|'));
  const wrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(wrapRef, 56, 280, [rows.length > 0, loading, !!genMsg]);

  const kpi = useMemo(() => {
    // Calendar view: the month shown (by the day each one sits on); List: the date range.
    const mKey = `${calYM.y}-${String(calYM.m + 1).padStart(2, '0')}`;
    const inW = viewMode === 'calendar'
      ? expenses.filter(e => calDayOf(e).startsWith(mKey))
      : window ? expenses.filter(e => inWindow(e.date, window)) : [];
    const total = inW.reduce((s, e) => s + (e.amountUSD ?? 0), 0);
    const unpaid = expenses.filter(e => e.paymentStatus === 'unpaid').reduce((s, e) => s + (e.amountUSD ?? 0), 0);
    const unpaidCount = expenses.filter(e => e.paymentStatus === 'unpaid').length;
    return { total, unpaid, unpaidCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, window, viewMode, calYM]);

  async function doUnpay(exp: Expense) {
    setBusy(true);
    const db = getClientDb();
    if (!db || !business) { setBusy(false); return; }
    try {
      const expRef = doc(db, 'expenses', exp.id);
      const accRef = exp.bankAccountId ? doc(db, 'bankAccounts', exp.bankAccountId) : null;
      await runTransaction(db, async tx => {
        const accSnap = accRef ? await tx.get(accRef) : null;
        tx.update(expRef, {
          paymentStatus: 'unpaid', paidUSD: 0,
          bankAccountId: null, bankAccountName: null, paidDate: null, bankTransactionId: null,
          updatedAt: serverTimestamp(),
        });
        if (accRef && accSnap?.exists()) {
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance + exp.amountUSD });
        }
        if (exp.bankTransactionId) tx.delete(doc(db, 'bankTransactions', exp.bankTransactionId));
        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'expense', sourceId: exp.id,
          sourceCollection: 'expenses', date: exp.date, currency: exp.currency,
          createdBy: user?.id ?? exp.createdBy, note: exp.description,
          lines: linesExpense(exp.amountUSD, exp.category, 'accrued', exp.vendorEntityId, paidGstPosting(exp)),
        }), entryId('expense', exp.id));
      });
    } finally { setBusy(false); setConfirm(null); }
  }

  async function doDelete(exp: Expense) {
    setBusy(true);
    const db = getClientDb();
    if (!db || !business) { setBusy(false); return; }
    try {
      const expRef = doc(db, 'expenses', exp.id);
      const accRef = exp.paymentStatus === 'paid' && exp.bankAccountId ? doc(db, 'bankAccounts', exp.bankAccountId) : null;
      await runTransaction(db, async tx => {
        const accSnap = accRef ? await tx.get(accRef) : null;
        tx.delete(expRef);
        if (accRef && accSnap?.exists()) {
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance + exp.amountUSD });
        }
        if (exp.bankTransactionId) tx.delete(doc(db, 'bankTransactions', exp.bankTransactionId));
        tx.delete(doc(db, 'journalEntries', entryId('expense', exp.id)));
      });
    } finally { setBusy(false); setConfirm(null); }
  }

  return (
    <AppLayout
      title="Expenses"
      subtitle="Running costs — rent, freight, salaries, fees. Feeds straight into Profit."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Category, description, vendor…" label="Search expenses" />
        {viewMode === 'list' && (
          <DateRangeButton
            value={dateKey} onChange={setDateKey}
            customFrom={customFrom} customTo={customTo}
            onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
          />
        )}
        {(searchQ || dateKey !== 'this-month' || colFilters.active.length > 0) && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-month'); colFilters.clear(); }}>Clear</button>
        )}
      </>}
      headerActions={<>
        <ViewToggle value={viewMode} onChange={setViewMode} />
        <Button variant="secondary" size="sm" onClick={() => setManageOpen(true)} style={{ height: 34, boxSizing: 'border-box' }}>Recurring</Button>
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setModal('add')}
          style={{ height: 34, boxSizing: 'border-box' }}>Add Expense</Button>
      </>}
    >
      {genMsg && <div className="msg msg-info" style={{ marginBottom: 12 }}>{genMsg}</div>}
      <StatChips items={[
        { label: 'Expenses', value: kpi.total > 0 ? money(kpi.total) : <span className="tb-stat-none">None</span> },
        { label: 'Bills to pay', tone: kpi.unpaid > 0 ? 'danger' : undefined, value: kpi.unpaid > 0
          ? <>{money(kpi.unpaid)} <span className="tb-stat-note">{kpi.unpaidCount} bill{kpi.unpaidCount !== 1 ? 's' : ''}</span></>
          : <span className="tb-stat-none">None</span> },
      ]} end={<ColumnFilterChips filters={colFilters} inline />} />

      {viewMode === 'calendar' ? (
        loading
          ? <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
          : <MoneyCalendar
              items={rows.map(e => {
                const day = calDayOf(e);
                const tone = e.paymentStatus === 'paid' ? 'paid' as const : day < localISO(new Date()) ? 'late' as const : 'due' as const;
                const who = e.vendorEntityId ? entityName(e.vendorEntityId) : '';
                return {
                  id: e.id, date: day, label: e.description || e.category, amount: e.amountUSD ?? 0, currency: e.currency, tone,
                  title: `${e.description || e.category}${who ? ` · ${who}` : ''} · ${fmtMoney(e.amountUSD ?? 0, e.currency)} · ${tone === 'paid' ? 'Paid' : tone === 'late' ? 'Overdue' : 'To pay'}`,
                };
              })}
              year={calYM.y} month={calYM.m} onPrev={() => stepMonth(-1)} onNext={() => stepMonth(1)}
              onSelect={id => { const e = rows.find(x => x.id === id); if (e) setModal({ edit: e }); }}
              tones={['paid', 'due', 'late']} summary="in expenses"
            />
      ) : loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>{baseRows.length ? 'Nothing matches these filters' : 'No expenses here'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>{baseRows.length ? 'Try clearing a filter.' : 'Add one with the button above.'}</div>
        </div>
      ) : (
        <>
          <div ref={wrapRef} className="table-wrap table-fit table-fit-stats">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 160 }} className="th-filterable">Category<ColumnFilterButton filters={colFilters} colKey="category" label="Category" /></th>
                  <th>Description</th>
                  <th style={{ width: 150 }} className="th-filterable">Vendor<ColumnFilterButton filters={colFilters} colKey="vendor" label="Vendor" /></th>
                  <th style={{ textAlign: 'right', width: 130 }}>Amount</th>
                  <th style={{ width: 90 }} className="th-filterable">Status<ColumnFilterButton filters={colFilters} colKey="status" label="Status" align="right" /></th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pg.pageRows.map(e => (
                  <tr key={e.id} onClick={() => setModal({ edit: e })} style={{ cursor: 'pointer' }}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(e.date)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{e.category}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {e.description}
                      {e.recurringExpenseId && (
                        <span style={{
                          marginLeft: 6, fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.04em', color: 'var(--primary)',
                          background: 'var(--primary-light)', padding: '2px 6px', borderRadius: 4,
                        }}>Recurring</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {e.vendorEntityId ? entityName(e.vendorEntityId) : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', textAlign: 'right', fontWeight: 600 }}>
                      {fmtMoney(e.amountUSD, e.currency)}
                    </td>
                    <td>
                      <span className="badge" style={{
                        fontSize: 10, fontWeight: 700,
                        background: e.paymentStatus === 'paid' ? 'var(--success-bg)' : 'var(--danger-bg)',
                        color: e.paymentStatus === 'paid' ? 'var(--success)' : 'var(--danger)',
                      }}>{e.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}</span>
                    </td>
                    <td className="col-actions" onClick={ev => ev.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {e.paymentStatus === 'unpaid'
                          ? <Button variant="secondary" size="sm" onClick={() => setModal({ pay: e })}>Pay</Button>
                          : <Button variant="ghost" size="sm" onClick={() => setConfirm({ kind: 'unpay', exp: e })}>Unpay</Button>}
                        <DeleteIconButton onClick={() => setConfirm({ kind: 'delete', exp: e })} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>Total · {rows.length} expense{rows.length !== 1 ? 's' : ''}</td>
                  <td style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>{money(rowsTotal)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
          <Paging page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} noun="expense" />
        </>
      )}

      {modal === 'add' && (
        <ExpenseModal editing={null} entities={entities} accounts={accounts} onClose={() => setModal(null)} />
      )}
      {modal && typeof modal === 'object' && 'edit' in modal && (
        <ExpenseModal editing={modal.edit} entities={entities} accounts={accounts} onClose={() => setModal(null)} />
      )}
      {modal && typeof modal === 'object' && 'pay' in modal && (
        <PayModal expense={modal.pay} accounts={accounts} onClose={() => setModal(null)} />
      )}
      {manageOpen && (
        <RecurringExpensesModal templates={recurringTemplates} entities={entities} accounts={accounts} onClose={() => setManageOpen(false)} />
      )}
      {confirm && (
        <ConfirmModal
          open
          title={confirm.kind === 'delete' ? 'Delete this expense?' : 'Mark as unpaid?'}
          message={confirm.kind === 'delete'
            ? `"${confirm.exp.description}" (${fmtMoney(confirm.exp.amountUSD, confirm.exp.currency)}) will be removed. Any linked bank movement is reversed.`
            : `The payment for "${confirm.exp.description}" will be reversed — the money goes back into ${confirm.exp.bankAccountName ?? 'the account'} and it becomes a bill again.`}
          confirmLabel={confirm.kind === 'delete' ? 'Delete' : 'Mark Unpaid'}
          loading={busy}
          onConfirm={() => (confirm.kind === 'delete' ? doDelete(confirm.exp) : doUnpay(confirm.exp))}
          onClose={() => setConfirm(null)}
        />
      )}
    </AppLayout>
  );
}
